import React, { Fragment } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Box, Button, createStyles, fade, IconButton, TextField, Theme, Tooltip, Typography } from "@material-ui/core";
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Classification, Locale, Participant } from "../../core/api/model";
import ConfirmDialog from "../../core/confirm-dialog";
import { makeStyles } from "@material-ui/core/styles";
import CustomIcon from "../../core/custom-icon";

export interface DashboardClassificationsListItemProps {
  locale: Locale;
  classification: Classification;
  experts: Participant[];
  deleteClassificationForced: (classificationId: number) => void;
  editClassification: (classificationId: number, partId: number | undefined) => void;
  disabled: boolean;
  lockItems: (classificationId: number | null) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    listItem: {
      paddingBottom: theme.spacing(2),
      '&:not(:first-child)': {
        paddingTop: theme.spacing(2)
      },
      '&:not(:last-child)': {
        borderBottom: `1px solid ${fade(theme.palette.primary.main, 0.1)}`
      }
    },
    actionSlot: {
      '& .MuiIconButton-root': {
        padding: 0,
        marginLeft: theme.spacing(3.5),

        [theme.breakpoints.up('md')]: {
          marginLeft: theme.spacing(3)
        }
      }
    },
    clearExpertButton: {
      padding: 0,
      marginLeft: theme.spacing(3),

      [theme.breakpoints.up('md')]: {
        marginLeft: theme.spacing(5)
      },
      '&:hover': {
        color: theme.palette.error.main
      }
    },
    deleteButton: {
      [theme.breakpoints.down('sm')]: {
        color: theme.palette.error.main
      },
      '&:hover': {
        color: theme.palette.error.main
      }
    },
    editCancelButton: {
      [theme.breakpoints.down('sm')]: {
        marginTop: theme.spacing(0.75)
      },
      [theme.breakpoints.up('md')]: {
        marginLeft: theme.spacing(-1)
      }
    },
    blockingOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      width: '100%',
      background: fade(theme.palette.common.white, 0.7),
      zIndex: theme.zIndex.appBar

    }
  }),
);

export const DashboardClassificationsListItem = (props: DashboardClassificationsListItemProps) => {

  const classes = useStyles();

  const intl = useIntl();

  const [editOpen, setEditOpen] = React.useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = React.useState<boolean>(false);
  const [selectedExpert, setSelectedExpert] = React.useState<Partial<Participant>>({});

  const onEdit = () => {
    setSelectedExpert(props.classification.expert);
    setEditOpen(true);
    props.lockItems(props.classification.classificationId);
  };

  const onEditClearParticipant = () => {
    setSelectedExpert({});
  }

  const onEditCancel = ()  => {
    setEditOpen(false);
    props.lockItems(null);
  }

  const onEditConfirm = () => {
    props.editClassification(props.classification.classificationId, selectedExpert.partId)
    setEditOpen(false);
    props.lockItems(null);
  }

  const onDelete = () => {
    setDeleteOpen(true);
  }

  return (

    <Fragment>
      <Box display='flex'
           className={classes.listItem}
           position='relative'
      >
        {props.disabled ? (
          <Box className={classes.blockingOverlay} />
        ) : null}

        <Box display='flex' flex='1 1 auto' flexDirection={{xs: 'column', md: 'row'}}>
          <Box pr={{md: 2.5}} flex={editOpen ? '0 0 50%' : '0 0 56%'}>
            <Typography component='h3' variant='h4'>
                {props.classification.classificationTitle[props.locale]}
            </Typography>
          </Box>
          <Box flex={editOpen ? '1 0 50%' : '1 0 auto'} pl={{md: 2.5}} mt={{xs: editOpen ? 3 : 2, md: `${0} !important`}}>

          {editOpen ? (
            <Box>
              <Box display='flex' alignItems='center'>
                <Box flexGrow='1'>
                  <Autocomplete
                    disableClearable
                    options={props.experts || []}
                    getOptionLabel={(expert: Partial<Participant>) => expert?.partName || expert?.partSurname ? `${expert?.partName} ${expert?.partSurname}` : ''}
                    value={selectedExpert}
                    onChange={(event, value) => {
                      setSelectedExpert(value);
                    }}
                    renderInput={(props) => <TextField {...props} variant="standard" label={intl.formatMessage({id: 'DashboardClassificationsAddExpertLabel'})} />}
                  />
                </Box>
                <Tooltip title={intl.formatMessage({id: 'DashboardClasificationsClearExpertOnProductButton'})} arrow placement='top'>
                  <IconButton aria-label={intl.formatMessage({id: 'DashboardClasificationsClearExpertOnProductButton'})}
                              color='primary'
                              className={classes.clearExpertButton}
                              onClick={onEditClearParticipant}
                  >
                    <CustomIcon iconname='delete' />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box display='flex'
                   flexDirection={{xs: 'column-reverse', md: 'row'}}
                   justifyContent='space-between'
                   mt={{xs: 3, md: 2}}
              >
                <Button onClick={onEditCancel}
                        variant='text'
                        className={classes.editCancelButton}
                >
                  <FormattedMessage id='DashboardClassificationsEditCancel' />
                </Button>
                <Button onClick={onEditConfirm}>
                  <FormattedMessage id='DashboardClasificationsEditConfirm' />
                </Button>
              </Box>

            </Box>

          ) : (
            props.classification.expert ? (
              <Fragment>
                <Box display='flex' component='p' mt={0}>
                  <CustomIcon iconname='person' viewBox='0 0 16 16' fontSize='small'/>
                  <Box component='span' ml={1}>
                    {props.classification.expert?.partName} {props.classification.expert?.partSurname}
                  </Box>
                </Box>

                <Box display='flex' component='p'>
                  <CustomIcon iconname='position' viewBox='0 0 16 16' fontSize='small'/>
                  <Box component='span' ml={1}>
                    {props.classification.expert?.partFunction}
                  </Box>
                </Box>
              </Fragment>
            ) : null
          )}

          </Box>
        </Box>
        {editOpen ? null : (
          <Box display='flex'
               flexShrink='0'
               flexGrow='0'
               alignItems={'flex-start'}
               flexDirection={{xs: 'column', md: 'row-reverse'}}
               justifyContent={'space-between'}
               className={classes.actionSlot}
          >
            <Tooltip title={intl.formatMessage({id: 'DashboardClasificationsDeleteProductButton'})} arrow placement='top'>
              <IconButton aria-label={intl.formatMessage({id: 'DashboardClasificationsDeleteProductButton'})}
                          color='primary'
                          className={classes.deleteButton}
                          onClick={onDelete}
              >
                <CustomIcon iconname='delete' />
              </IconButton>
            </Tooltip>

              <Tooltip title={intl.formatMessage({id: 'DashboardClasificationsEditExpertButton'})} arrow placement='top'>
                <IconButton aria-label='Experten bearbeiten'
                            color='primary'
                            onClick={onEdit}
                >
                  <CustomIcon iconname='edit' />
                </IconButton>
              </Tooltip>

          </Box>
        )}
      </Box>

      <ConfirmDialog
        title={intl.formatMessage({id: 'DashboardClassificationsDeleteTitle'})}
        confirmText={intl.formatMessage({id: 'DashboardClasificationsDeleteConfirm'})}
        cancelText={intl.formatMessage({id: 'DashboardClassificationsDeleteCancel'})}
        open={deleteOpen}
        setOpen={setDeleteOpen}
        onConfirm={() => props.deleteClassificationForced(props.classification.classificationId)}
      >
        <FormattedMessage id='DashboardClassificationsDeleteDescription' />
      </ConfirmDialog>

    </Fragment>
  )
};
